import { useCalcPrice } from "@/talons";
import { CartItems } from "./CartContext";
import { HandlePrices, calcPrice } from "@/shared/calculatePrices";
import { isNroSegmentoPrecoCuringa } from "@/shared";
import { PriceProductProps, TaxSubstitutionProps } from "@/types";
import { useMemo, useContext, useCallback, createContext} from "react";
import {  useClient,  useTables,  useGlobals,  useCart,  useDiscountFlex,  useDiscountAmountFlex, useCorporateFlags } from "@/hooks";

interface CartToTalsProps {
  cartTotals: {
    flex: any;
    total: number;
    subtotal: number;
    st_price: number;
  };
  fetchCorrectPrice: (
    price: PriceProductProps[],
    option_uid: string,
    paymentCondition: number
  ) => PriceProductProps;
  fetchTotalPriceByPaymentCode: (code: number) => number;
  listaTributacao: TaxSubstitutionProps[];
}

export const CartTotalsContext = createContext({} as CartToTalsProps);

const CartTotalsProvider = ({ children }: React.PropsWithChildren) => {
  const { tables } = useTables();
  const { getPrices, findPrice } = useCalcPrice();
  const products = tables.current.dicionario_produtos;
  const listaTributacao = tables.current.cod_regime_tributario;

  const { currentCartDetails } = useCart();
  const { paymentCode } = useGlobals();
  const { selectedClient } = useClient();
  const { selectedBrandPair } = useCorporateFlags();
  const { getDiscountProduct } = useDiscountFlex();
  const { discountAmountClient } = useDiscountAmountFlex();

  const selectedClientScope = useMemo(() => {
    if (!selectedClient) return null;
    if (!selectedBrandPair?.codLoja || !selectedBrandPair?.codSegmento) return selectedClient;

    const lojas = Array.isArray(selectedClient.lojas) ? selectedClient.lojas : [];
    const matchedLoja = lojas.find((loja: any) => {
      if (Number(loja?.COD_LOJA) !== Number(selectedBrandPair.codLoja)) return false;
      const segmentosLoja = Array.isArray(loja?.COD_SEGMENTO) ? loja.COD_SEGMENTO : [];
      return segmentosLoja.some((segmento: any) => {
        return Number(segmento?.cod_segmento ?? segmento?.COD_SEGMENTO ?? segmento) === Number(selectedBrandPair.codSegmento);
      });
    });

    if (!matchedLoja) return selectedClient;

    const segmentosLoja = Array.isArray(matchedLoja?.COD_SEGMENTO) ? matchedLoja.COD_SEGMENTO : [];
    const segmentoAtivo = segmentosLoja.find((segmento: any) => {
      return Number(segmento?.cod_segmento ?? segmento?.COD_SEGMENTO ?? segmento) === Number(selectedBrandPair.codSegmento);
    });

    return {
      ...selectedClient,
      ...matchedLoja,
      COD_SEGMENTO: segmentoAtivo ? [segmentoAtivo] : segmentosLoja,
    };
  }, [selectedClient, selectedBrandPair]);

  const fetchCorrectPrice = useCallback(
    (
      price: PriceProductProps[],
      option_uid: string,
      paymentCondition: number
    ) => {
      return findPrice(price, option_uid, paymentCondition);
    },
    [findPrice]
  );

  const fetchTotalPriceByPaymentCode = useCallback(
    (code: number) => {
      if (
        currentCartDetails?.items?.length > 0 &&
        code
      ) {
        const totals = currentCartDetails?.items?.reduce((acc: number, item: CartItems) => {
          if (!products?.[item?.sku]) return acc;
          const prices = getPrices(products?.[item?.sku]);
          
          if (!!prices) {
            const [packing, qtd] = item?.option_uid?.split("_");

            const flexEntry = getDiscountProduct({
              package: packing,
              productId: item?.sku,
              quantity: Number(qtd),
            });
            const percentualFlex = Number(flexEntry?.percentual ?? 0);
            const rawDiscount = flexEntry?.discountPrice?.[`${code}`];

            const price = prices
              .filter((p: PriceProductProps) => p.COD_LOJA === selectedClientScope?.COD_LOJA)
              ?.find((i: PriceProductProps) => {
                const clientSegs = selectedClientScope?.COD_SEGMENTO?.map((s: any) => s.cod_segmento ?? s.COD_SEGMENTO ?? s) || [];
                if (i?.QTD_MULTIPLO_VENDA > 1) {
                  return (
                    i?.QTD_MULTIPLO_VENDA?.toString() === qtd &&
                    i?.SG_EMBALAGEM === packing &&
                    i?.NROCONDICAOPAGTO === code &&
                    (isNroSegmentoPrecoCuringa(i?.NRO_SEGMENTO) ||
                      clientSegs.some((s: number) => Number(s) === Number(i?.NRO_SEGMENTO)))
                  );
                }
                return (
                  i?.QTD_EMBALAGEM?.toString() === qtd &&
                  i?.SG_EMBALAGEM === packing &&
                  i?.NROCONDICAOPAGTO === code &&
                  (isNroSegmentoPrecoCuringa(i?.NRO_SEGMENTO) ||
                    clientSegs.some((s: number) => Number(s) === Number(i?.NRO_SEGMENTO)))
                );
              });

            let finalprice: number | undefined;
            if (rawDiscount !== undefined && rawDiscount !== null) {
              finalprice = Number(rawDiscount);
            } else if (
              percentualFlex !== 0 &&
              price &&
              typeof price.VLR_PRECO === "number" &&
              listaTributacao?.length
            ) {
              const adjustedRow: PriceProductProps = {
                ...price,
                VLR_PRECO: price.VLR_PRECO + (price.VLR_PRECO / 100) * percentualFlex,
              };
              const calculated = calcPrice(selectedClientScope, [adjustedRow], listaTributacao);
              finalprice = calculated[0]?.VLR_FINAL;
            }
            if (finalprice === undefined || finalprice === null || Number.isNaN(finalprice)) {
              finalprice = price?.VLR_FINAL || price?.VLR_PRECO;
            }

            if (finalprice) {
              const fullprice = finalprice * item?.quantity;
              if (fullprice) return (acc += fullprice);
            }
          }
          return acc;
        }, 0);
        return totals;
      }
      return 0;
    },
    [currentCartDetails, products, getDiscountProduct, selectedClientScope, listaTributacao, getPrices]
  );

  const handleCalcStTotals = useCallback((code: number) => {
    if (currentCartDetails?.items?.length > 0 && code) {
      const stTotals = currentCartDetails?.items?.reduce((acc: number, item: CartItems) => {
        if (!products?.[item?.sku]) return acc;
        const prices = getPrices(products?.[item?.sku]);

        if (!!prices.length) {
          const [packing, qtd] = item?.option_uid?.split("_");

          const price = prices
            .filter((p: PriceProductProps) => p.COD_LOJA === selectedClientScope?.COD_LOJA)
            .find((i: PriceProductProps) => {
              const clientSegs = selectedClientScope?.COD_SEGMENTO?.map((s: any) => s.cod_segmento ?? s.COD_SEGMENTO ?? s) || [];

              if (i?.QTD_MULTIPLO_VENDA > 1) {
                return (
                  i?.QTD_MULTIPLO_VENDA?.toString() === qtd &&
                  i?.SG_EMBALAGEM === packing &&
                  i?.NROCONDICAOPAGTO === code &&
                  (isNroSegmentoPrecoCuringa(i?.NRO_SEGMENTO) ||
                    clientSegs.some((s: number) => Number(s) === Number(i?.NRO_SEGMENTO)))
                );
              }
              return (
                i?.QTD_EMBALAGEM?.toString() === qtd &&
                i?.SG_EMBALAGEM === packing &&
                i?.NROCONDICAOPAGTO === code &&
                ((isNroSegmentoPrecoCuringa(i?.NRO_SEGMENTO) && clientSegs.some((s) => s === 60 || s === 61)) ||
                  clientSegs.some((s: number) => Number(s) === Number(i?.NRO_SEGMENTO)) ||
                  (isNroSegmentoPrecoCuringa(i?.NRO_SEGMENTO) && clientSegs.length === 0))
              );
            });

          if (price?.DESTACA_ST === 'S' && selectedClientScope?.COD_LOJA === 743 && listaTributacao?.length && price?.VLR_PRECO_CALCULADO) {
            const tributacao = HandlePrices.getTributacao(selectedClientScope, price?.COD_TRIBUTACAO_PRODUTO, listaTributacao);

            const precoSt = HandlePrices.CalculaST(tributacao, price?.VLR_PRECO_CALCULADO * item?.quantity, price?.VLR_PAUTA);

            if (precoSt) {
              return acc += precoSt;
            }
            return acc;
          }
          return acc;
        }
        return acc;
      }, 0);
      return stTotals;
    }
    return 0;
  }, [currentCartDetails, products, listaTributacao, selectedClientScope, getPrices]);

  const cartTotals = useMemo(() => {
    const total = fetchTotalPriceByPaymentCode(paymentCode);
    const stTotals = handleCalcStTotals(paymentCode);
    return {
      total,
      subtotal: total - stTotals,
      st_price: stTotals,
      flex: discountAmountClient || 0,
    }
  }, [paymentCode, fetchTotalPriceByPaymentCode, handleCalcStTotals]);

  return (
    <CartTotalsContext.Provider value={{
      cartTotals,
      fetchCorrectPrice,
      fetchTotalPriceByPaymentCode,
      listaTributacao,
    }}>
      {children}
    </CartTotalsContext.Provider>
  );
};

const useCartTotals = () => {
  const context = useContext(CartTotalsContext);

  if (!context) {
    throw new Error(
      "o context useCartTotals só pode ser utilizado dentro de seu escopo. Erro ao usar hook useCartTotals"
    );
  }
  return context;
};

export { CartTotalsProvider, useCartTotals };
